/**
 * LegalShell — the page chrome used by all static legal pages.
 *
 * Provides: a slim header with the brand + "Volver" link, the SiteFooter,
 * and a centred container that wraps the page body so we get consistent
 * typography and spacing without each legal page redefining it.
 *
 * Layout choice: legal pages are content-heavy and meant to be deep-linked
 * from elsewhere (footer, cart checkbox, support emails). They get their
 * own URL and their own shell, but share the public SiteFooter so visitors
 * can navigate to other sections without going through the homepage.
 */

import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { Brand } from '@/components/Brand';
import { SiteFooter } from './SiteFooter';

interface LegalShellProps {
  eyebrow: string;
  title: string;
  /** Optional "last updated" line under the title. */
  updated?: string;
  /** "Volver" target — defaults to `/`. Legal pages linked from the
   *  cart drawer pass `/planes` so the user returns to checkout. */
  backTo?: string;
  backLabel?: string;
  children: ReactNode;
}

export function LegalShell({
  eyebrow,
  title,
  updated,
  backTo = '/',
  backLabel = 'Volver al inicio',
  children,
}: LegalShellProps) {
  return (
    <div className="min-h-screen flex flex-col" style={{ background: 'var(--surface-base)', color: 'var(--text-primary)' }}>
      <header
        className="sticky top-0 z-30 flex h-16 items-center justify-between border-b px-6 backdrop-blur-md"
        style={{ background: 'rgba(2,6,23,0.7)', borderColor: 'var(--border-subtle)' }}
      >
        <Link to="/" className="no-underline">
          <Brand size="md" />
        </Link>
        <Link
          to={backTo}
          className="inline-flex h-9 items-center gap-1.5 rounded-[var(--radius)] border px-3 text-sm no-underline transition-colors hover:bg-[var(--surface-2)]"
          style={{ borderColor: 'var(--border-default)', color: 'var(--text-secondary)' }}
        >
          <ArrowLeft size={14} strokeWidth={1.8} /> {backLabel}
        </Link>
      </header>

      <main className="flex-1">
        <div className="mx-auto max-w-3xl px-6 py-12">
          <div className="text-[11px] uppercase tracking-[0.18em]" style={{ color: 'var(--text-muted)' }}>
            {eyebrow}
          </div>
          <h1
            className="mt-1 text-3xl md:text-4xl"
            style={{ fontFamily: 'var(--font-display)', fontWeight: 700, letterSpacing: '-0.02em' }}
          >
            {title}
          </h1>
          {updated && (
            <p className="mt-2 text-[12px]" style={{ color: 'var(--text-muted)' }}>
              {updated}
            </p>
          )}

          <div className="mt-8 space-y-6 text-[14px]" style={{ color: 'var(--text-secondary)', lineHeight: 1.7 }}>
            {children}
          </div>
        </div>
      </main>

      <SiteFooter />
    </div>
  );
}

/** Small helper used inside the legal pages to keep markup consistent. */
export function LegalSection({ title, children }: { title: string; children: ReactNode }) {
  return (
    <section>
      <h2
        className="mb-2 text-lg"
        style={{ color: 'var(--text-primary)', fontFamily: 'var(--font-display)', fontWeight: 700 }}
      >
        {title}
      </h2>
      <div className="space-y-2">{children}</div>
    </section>
  );
}
